import useMountEffect from "@p-ban/common/src/hooks/useMountEffect"
import type { BooleanRecord } from "@p-ban/common/src/types"

import notify from "../../../../../utils/notify"
import ESTIMATE_CONSTANT from "../../../constant/estimateConstant"
import estimateField from "../../../service/estimateFields/estimateField"
import fillRestWithInitialValues from "../../../service/fillRestWithInitialValues"
import getDisplayCondition from "../../../service/getDisplayCondition"
import type { Touched } from "../../../types"
import type EstimateValues from "../../../types/params"
import { getValuesFromShareLink } from "../shareLink"
import temporaryStorage from "../temporaryStorage"

const useRestoreValues = (
  setValuesAndTouched: React.Dispatch<React.SetStateAction<{ values: EstimateValues; touched: Touched }>>,
  setBomSnapshotId: React.Dispatch<React.SetStateAction<string | null>>,
  setDeliveryZeroFlg: React.Dispatch<React.SetStateAction<boolean>>,
  setMountQuickFlg: React.Dispatch<React.SetStateAction<boolean>>,
  setMetalMaskStockPartName: React.Dispatch<React.SetStateAction<string | null>>,
  setBoardStockPartName: React.Dispatch<React.SetStateAction<string | null>>,
) => {
  useMountEffect(() => {
    // 部品調達から戻ってきた場合、その内容を反映します。
    if (window.location.hash === "#bomParsingFlg") {
      const {
        values: restoredValues,
        touched: restoredTouched,
        bomSnapshotId: restoredBomId,
        deliveryZeroFlg: restoredDeliveryZeroFlg,
        mountQuickFlg: restoredMountQuickFlg,
      } = temporaryStorage.restore()
      const {
        values: valuesFromProcurement,
        bomSnapshotId: linkedBomId,
        deliveryZeroFlg: linkedDeliveryZeroFlg,
        mountQuickFlg: linkedMountQuickFlg,
        metalMaskStockPartName: linkedMetalMaskName,
        boardStockPartName: linkedBoardName,
      } = getValuesFromShareLink(window.location.search)
      setBomSnapshotId(restoredBomId ?? linkedBomId)
      setDeliveryZeroFlg(restoredDeliveryZeroFlg ?? linkedDeliveryZeroFlg)
      setMountQuickFlg(restoredMountQuickFlg ?? linkedMountQuickFlg)
      setMetalMaskStockPartName(linkedMetalMaskName)
      setBoardStockPartName(linkedBoardName)

      const mergedValues = { ...restoredValues, ...valuesFromProcurement }
      // GHメタルマスク在庫選択時: リフロー実装 + PBAN を強制セット
      const ghMetalMaskValues: Partial<EstimateValues> = {}
      if (linkedMetalMaskName) {
        mergedValues.metalmask = ESTIMATE_CONSTANT.実装方法.リフロー実装をする.value
        mergedValues.metalMaskDtl = ESTIMATE_CONSTANT["メタルマスクの製造・支給選択"].GugenHub在庫.value
        ghMetalMaskValues.metalmask = mergedValues.metalmask
        ghMetalMaskValues.metalMaskDtl = mergedValues.metalMaskDtl
      }

      setValuesAndTouched({
        values: fillRestWithInitialValues(mergedValues),
        touched: { ...restoredTouched, ...getTouched(valuesFromProcurement), ...getTouched(ghMetalMaskValues) },
      })
    }
    // クエリパラメータ付きの共有リンクから入力項目を復元します。
    else if (window.location.search) {
      const {
        values: valuesFromShareLink,
        unknownParams,
        bomSnapshotId,
        deliveryZeroFlg,
        mountQuickFlg,
        metalMaskStockPartName,
        boardStockPartName,
      } = getValuesFromShareLink(window.location.search)
      setBomSnapshotId(bomSnapshotId)
      setDeliveryZeroFlg(deliveryZeroFlg)
      setMountQuickFlg(mountQuickFlg)
      setMetalMaskStockPartName(metalMaskStockPartName)
      setBoardStockPartName(boardStockPartName)

      // GHメタルマスク在庫選択時: リフロー実装 + PBAN を強制セット
      if (metalMaskStockPartName) {
        valuesFromShareLink.metalmask = ESTIMATE_CONSTANT.実装方法.リフロー実装をする.value
        valuesFromShareLink.metalMaskDtl = ESTIMATE_CONSTANT["メタルマスクの製造・支給選択"].GugenHub在庫.value
      }

      const values = fillRestWithInitialValues(valuesFromShareLink)
      notifyUnknownParams(unknownParams, values)
      setValuesAndTouched({
        values,
        touched: getTouched(valuesFromShareLink),
      })
    }
  })
}

export default useRestoreValues

const getTouched = (values: Partial<EstimateValues>) => {
  const touched: Touched = {}
  for (const field of Object.keys(values) as (keyof EstimateValues)[]) {
    touched[field] = true
  }
  return touched
}

const notifyUnknownParams = (unknownParams: Partial<BooleanRecord<EstimateValues>>, values: EstimateValues) => {
  const messages: string[] = []

  const isDisplayed = getDisplayCondition(values)
  const entries = Object.entries(unknownParams) as [keyof EstimateValues, boolean | undefined][]

  entries.forEach(([name, isUnknown]) => {
    if (isDisplayed[name] && isUnknown) {
      const displayName = estimateField[name].displayName(values)
      messages.push(`・${displayName}`)
    }
  })

  if (messages.length) {
    messages.unshift("不明な値が指定されたため、以下の項目を標準の値に変更しました")
    notify(messages.join("\n"))
  }
}
